import { motion } from 'framer-motion'
import Container from '@comp/ui/container'
import Section from '@comp/ui/section'
import Eyebrow from '@comp/ui/eyebrow'
import Heading from '@comp/ui/heading'
import Text from '@comp/ui/text'

const EASE = [0.22, 1, 0.36, 1] as const

const dos = [
  'Use the files we provide; do not recreate the logo by tracing or redrawing.',
  'Use the wordmark whenever there is room. Reach for the square mark only when the wordmark would not be legible.',
  'Place the logo on one of the three approved surfaces: Madison Blue, White, or Eerie Black.',
  'Maintain clear space around the logo equal to at least the height of the mark.',
]

const donts = [
  { title: 'Do not crop the logo', detail: 'Keep the full mark or wordmark in frame at all times.' },
  { title: 'Do not change transparency', detail: 'The logo is always rendered at 100% opacity.' },
  { title: 'Do not recolor the logo', detail: 'Only the approved color pairings are permitted.' },
  { title: 'Do not distort the logo', detail: 'Never stretch, skew, or warp the proportions.' },
  { title: 'Do not add effects', detail: 'No drop shadows, glows, bevels, or outlines.' },
  { title: 'Do not change the typeface', detail: 'The wordmark uses a custom letterform; leave it alone.' },
  { title: 'Do not outline the logo', detail: 'Strokes or borders on the logo glyphs are not permitted.' },
]

export default function UsageGuidelines() {
  return (
    <Section tone="muted" density="default" id="usage">
      <Container size="xl">
        <div className="grid gap-12 lg:grid-cols-[1fr_1.4fr] lg:gap-16">
          {/* Do column */}
          <div>
            <Eyebrow>Do</Eyebrow>
            <Heading as="h2" size="display-md" className="mt-5">
              Follow these
              <br />
              every time.
            </Heading>
            <ul className="mt-10 space-y-5">
              {dos.map((d) => (
                <li key={d} className="flex gap-3">
                  <CheckIcon className="mt-1 h-5 w-5 flex-shrink-0 text-accent-600" />
                  <Text size="md" tone="secondary">
                    {d}
                  </Text>
                </li>
              ))}
            </ul>
          </div>

          {/* Don't column */}
          <div>
            <Eyebrow tone="accent">Don&apos;t</Eyebrow>
            <Heading as="h2" size="display-md" className="mt-5">
              Seven things
              <br />
              that break the brand.
            </Heading>
            <div className="mt-10 grid gap-4 sm:grid-cols-2">
              {donts.map((d, i) => (
                <motion.div
                  key={d.title}
                  initial={{ opacity: 0, y: 12 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.45, ease: EASE, delay: (i % 2) * 0.05 }}
                  className="rounded-2xl border border-line-subtle bg-surface p-5"
                >
                  <div className="flex items-start gap-3">
                    <CrossIcon className="mt-0.5 h-5 w-5 flex-shrink-0 text-red-500" />
                    <div className="min-w-0">
                      <div className="text-body-md font-medium text-ink-primary">
                        {d.title}
                      </div>
                      <div className="mt-1 text-body-sm text-ink-tertiary">
                        {d.detail}
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </Container>
    </Section>
  )
}

function CheckIcon({ className = '' }) {
  return (
    <svg className={className} viewBox="0 0 20 20" fill="none">
      <circle cx="10" cy="10" r="10" fill="currentColor" fillOpacity="0.15" />
      <path d="M5.6 10.2 8.6 13.2 14.4 7.4" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}

function CrossIcon({ className = '' }) {
  return (
    <svg className={className} viewBox="0 0 20 20" fill="none">
      <circle cx="10" cy="10" r="10" fill="currentColor" fillOpacity="0.15" />
      <path d="M7 7l6 6M13 7l-6 6" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
    </svg>
  )
}
